import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import type { SuperAdminFormValues, SuperAdminRecord, SuperAdminUpdateValues } from '../types';
import './SuperAdminFormModal.scss';

const createSchema = z.object({
  email: z.string().email('Email không hợp lệ'),
  password: z
    .string()
    .min(8, 'Mật khẩu tối thiểu 8 ký tự')
    .max(32, 'Mật khẩu tối đa 32 ký tự')
    .optional()
    .or(z.literal('')),
  user_name: z.string().min(3, 'Tên đăng nhập tối thiểu 3 ký tự').max(50, 'Tên đăng nhập tối đa 50 ký tự').optional(),
  full_name: z.string().max(120, 'Họ tên tối đa 120 ký tự').optional(),
  phone: z.string().max(20, 'Số điện thoại tối đa 20 ký tự').optional(),
  status: z.enum(['active', 'inactive']),
  sendInvite: z.boolean().optional(),
});

const updateSchema = createSchema
  .omit({ email: true, password: true })
  .extend({
    full_name: z.string().max(120, 'Họ tên tối đa 120 ký tự').optional().or(z.literal('')),
    phone: z.string().max(20, 'Số điện thoại tối đa 20 ký tự').optional().or(z.literal('')),
  })
  .partial({ status: true });

type Mode = 'create' | 'edit';

interface SuperAdminFormModalProps {
  open: boolean;
  mode: Mode;
  onClose: () => void;
  onSubmitCreate: (values: SuperAdminFormValues) => Promise<void> | void;
  onSubmitUpdate: (id: string, payload: SuperAdminUpdateValues) => Promise<void> | void;
  initialData?: SuperAdminRecord | null;
  isSubmitting?: boolean;
}

export function SuperAdminFormModal({
  open,
  mode,
  onClose,
  onSubmitCreate,
  onSubmitUpdate,
  initialData,
  isSubmitting = false,
}: SuperAdminFormModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SuperAdminFormValues>({
    resolver: zodResolver(mode === 'create' ? createSchema : updateSchema),
    defaultValues: {
      email: '',
      password: '',
      user_name: '',
      full_name: '',
      phone: '',
      status: 'active',
      sendInvite: true,
    },
  });

  useEffect(() => {
    if (open) {
      if (mode === 'edit' && initialData) {
        reset({
          email: initialData.email,
          full_name: initialData.full_name ?? '',
          user_name: initialData.user_name ?? '',
          phone: initialData.phone ?? '',
          status: initialData.status,
          sendInvite: false,
        });
      } else if (mode === 'create') {
        reset({
          email: '',
          password: '',
          user_name: '',
          full_name: '',
          phone: '',
          status: 'active',
          sendInvite: true,
        });
      }
    } else {
      reset();
    }
  }, [open, mode, initialData, reset]);

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [open]);

  const submitHandler = handleSubmit(async (values) => {
    if (mode === 'create') {
      await onSubmitCreate({
        ...values,
        status: values.status ?? 'active',
      });
    } else if (initialData) {
      await onSubmitUpdate(initialData.id, {
        full_name: values.full_name,
        phone: values.phone,
        status: values.status,
      });
    }
    onClose();
  });

  if (!open) return null;

  return (
    <div className="hrm-modal-overlay">
      <div className="hrm-modal" role="dialog" aria-modal="true">
        <div className="modal-dialog">
          <div className="hrm-modal-content modal-content super-admin-form-modal">
            <div className="modal-header super-admin-form-modal__header">
              <div className="header-stack">
                <span className="header-icon">
                  <i className="ti ti-shield-check" />
                </span>
                <div>
                  <h5 className="modal-title mb-1">
                    {mode === 'create' ? 'Thêm Super Admin' : 'Chỉnh sửa Super Admin'}
                  </h5>
                  <p className="header-subtitle mb-0">
                    {mode === 'create'
                      ? 'Cấp quyền quản trị cấp cao cho tài khoản mới.'
                      : 'Cập nhật thông tin hiển thị của Super Admin.'}
                  </p>
                </div>
              </div>
              <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
            </div>

            <form onSubmit={submitHandler}>
              <div className="modal-body">
                <div className="form-section">
                  <div className="section-heading">
                    <span className="section-icon">
                      <i className="ti ti-user-shield" />
                    </span>
                    <div>
                      <p className="section-label">Thông tin tài khoản</p>
                      <h6>Đăng nhập và xác thực</h6>
                    </div>
                  </div>

                  <div className="form-grid form-grid--two">
                    <div className="form-field">
                      <label className={`form-label ${mode === 'create' ? 'required' : ''}`}>Email</label>
                      <input
                        type="email"
                        className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                        placeholder="admin@example.com"
                        {...register('email')}
                        disabled={mode === 'edit'}
                      />
                      {errors.email && <span className="text-danger small">{errors.email.message}</span>}
                    </div>

                    {mode === 'create' && (
                      <div className="form-field">
                        <label className="form-label">Mật khẩu tạm</label>
                        <input
                          type="password"
                          className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                          placeholder="Nhập mật khẩu hoặc để trống để tạo tự động"
                          {...register('password')}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-section">
                  <div className="section-heading">
                    <span className="section-icon">
                      <i className="ti ti-user" />
                    </span>
                    <div>
                      <p className="section-label">Thông tin cá nhân</p>
                      <h6>Họ tên, tên đăng nhập và liên hệ</h6>
                    </div>
                  </div>

                  <div className="form-grid form-grid--two">
                    <div className="form-field">
                      <label className="form-label">Tên đăng nhập</label>
                      <input
                        type="text"
                        className={`form-control ${errors.user_name ? 'is-invalid' : ''}`}
                        placeholder="Tên đăng nhập"
                        {...register('user_name')}
                      />
                      {errors.user_name && <span className="text-danger small">{errors.user_name.message}</span>}
                    </div>

                    <div className="form-field">
                      <label className="form-label">Họ tên</label>
                      <input
                        type="text"
                        className={`form-control ${errors.full_name ? 'is-invalid' : ''}`}
                        placeholder="Họ tên đầy đủ"
                        {...register('full_name')}
                      />
                      {errors.full_name && <span className="text-danger small">{errors.full_name.message}</span>}
                    </div>

                    <div className="form-field">
                      <label className="form-label">Số điện thoại</label>
                      <input
                        type="text"
                        className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                        placeholder="Số điện thoại"
                        {...register('phone')}
                      />
                      {errors.phone && <span className="text-danger small">{errors.phone.message}</span>}
                    </div>

                    <div className="form-field">
                      <label className="form-label">Trạng thái</label>
                      <select className="form-select" {...register('status')}>
                        <option value="active">Đang hoạt động</option>
                        <option value="inactive">Vô hiệu hóa</option>
                      </select>
                    </div>
                  </div>
                </div>

                {mode === 'create' && (
                  <div className="form-section">
                    <div className="section-heading">
                      <span className="section-icon">
                        <i className="ti ti-mail" />
                      </span>
                      <div>
                        <p className="section-label">Thông báo</p>
                        <h6>Tùy chọn gửi email mời</h6>
                      </div>
                    </div>

                    <div className="form-check">
                      <input
                        id="sendInvite"
                        type="checkbox"
                        className="form-check-input"
                        defaultChecked
                        {...register('sendInvite')}
                      />
                      <label htmlFor="sendInvite" className="form-check-label">
                        Gửi email mời đăng nhập
                      </label>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={isSubmitting}>
                  Hủy
                </button>
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Đang lưu…' : mode === 'create' ? 'Tạo mới' : 'Cập nhật'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}